import { embaralhar, type Sorteio } from "./fila.js";

/**
 * A ordem das músicas da galeria, sem áudio nenhum.
 *
 * Separado do `<audio>` pelo mesmo motivo da fila das fotos: é aqui que moram
 * as regras — sequência ou sorteio, repetir ou parar, a lista que muda no meio
 * —, e regra se testa sem navegador.
 */
export interface EstadoTocador {
  /** Ids das faixas, na ordem desta volta. */
  ordem: number[];
  posicao: number;
  /** Sem repetir, a lista acabou e a galeria segue em silêncio. */
  acabou: boolean;
}

export interface ModoTocador {
  aleatoria: boolean;
  repetir: boolean;
}

export const TOCADOR_PARADO: EstadoTocador = { ordem: [], posicao: 0, acabou: true };

/**
 * Uma volta: na ordem da lista, ou embaralhada. Embaralhada, a primeira não
 * repete a última da volta anterior — a mesma música duas vezes seguidas
 * parece defeito.
 */
export function montarVolta(
  ids: readonly number[],
  aleatoria: boolean,
  ultima: number | null = null,
  aleatorio: Sorteio = Math.random,
): number[] {
  if (!aleatoria) return [...ids];

  const volta = embaralhar(ids, aleatorio);
  if (volta.length > 1 && volta[0] === ultima) {
    [volta[0], volta[1]] = [volta[1]!, volta[0]!];
  }
  return volta;
}

export function iniciarTocador(
  ids: readonly number[],
  modo: ModoTocador,
  aleatorio: Sorteio = Math.random,
): EstadoTocador {
  if (ids.length === 0) return TOCADOR_PARADO;
  return { ordem: montarVolta(ids, modo.aleatoria, null, aleatorio), posicao: 0, acabou: false };
}

/** A faixa de agora, ou nada. */
export function faixaAtual(e: EstadoTocador): number | null {
  if (e.acabou) return null;
  return e.ordem[e.posicao] ?? null;
}

/**
 * A próxima. No fim da volta, repetir monta outra (reembaralhada, se for o
 * caso); sem repetir, acaba.
 */
export function proximaFaixa(
  e: EstadoTocador,
  ids: readonly number[],
  modo: ModoTocador,
  aleatorio: Sorteio = Math.random,
): EstadoTocador {
  if (e.acabou || ids.length === 0) return { ...e, acabou: true };

  if (e.posicao + 1 < e.ordem.length) return { ...e, posicao: e.posicao + 1 };
  if (!modo.repetir) return { ...e, acabou: true };

  return {
    ordem: montarVolta(ids, modo.aleatoria, e.ordem[e.posicao] ?? null, aleatorio),
    posicao: 0,
    acabou: false,
  };
}

/**
 * A lista mudou no meio — a consulta voltou com a playlist editada, ou com uma
 * música apagada. A que está tocando continua; as que sumiram saem do resto da
 * volta, e as novas entram no fim.
 */
export function sincronizarFaixas(e: EstadoTocador, ids: readonly number[]): EstadoTocador {
  if (e.acabou) return e;

  const existe = new Set(ids);
  const atual = e.ordem[e.posicao];
  const antes = e.ordem.slice(0, e.posicao).filter((id) => existe.has(id));
  const depois = e.ordem.slice(e.posicao + 1).filter((id) => existe.has(id));
  const novas = ids.filter((id) => !e.ordem.includes(id));

  const ordem = [...antes, ...(atual !== undefined ? [atual] : []), ...depois, ...novas];
  const mudou = ordem.length !== e.ordem.length || ordem.some((id, i) => id !== e.ordem[i]);
  return mudou ? { ...e, ordem, posicao: antes.length } : e;
}
